import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import "./layout.css";

export interface DrawerProps {
  open?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Sits in the header, to the left of the close button. */
  actions?: ReactNode;
  footer?: ReactNode;
  side?: "left" | "right";
  className?: string;
  /** Wider panel for forms that list a full playbook, such as sales-cycle tasks. */
  wide?: boolean;
  width?: number;
}

/**
 * Slide-in panel anchored to a screen edge. `open` is the IMS/dashboard
 * designer API; `isOpen` is kept as an alias for existing callers.
 */
export function Drawer({
  open,
  isOpen,
  onClose,
  title,
  children,
  actions,
  footer,
  side = "right",
  className,
  wide = false,
}: DrawerProps) {
  const shown = open ?? isOpen ?? false;

  useEffect(() => {
    if (!shown) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [shown, onClose]);

  if (!shown) return null;

  return createPortal(
    <div className={`ui-drawer__backdrop${className ? ` ${className}` : ""}`} onMouseDown={onClose}>
      <div
        className={`ui-drawer ui-drawer--${side}${wide ? " ui-drawer--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ui-drawer__header">
          {title ? <h2 className="ui-drawer__title">{title}</h2> : <span />}
          <div className="ui-drawer__header-actions">
            {actions}
            <button type="button" className="ui-drawer__close" onClick={onClose} aria-label="Close panel">
              ×
            </button>
          </div>
        </div>
        <div className="ui-drawer__body">{children}</div>
        {footer ? <div className="ui-drawer__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
